import React from 'react';
import { SubmitButton, Header4, Button, ButtonColor, ButtonSize, useFormContext, SimpleBlock } from 'ui-builder';
import FormTemplate from '../../../shared/uibuilder/form/FormTemplate';
import Row6x6x from '../../../shared/web/uitheme/form/Row6x6x';
import { ColorPickerWithText } from './ColorPickerWithText';
import { Theme } from '../Domain/clientPortalService';
import { DEFAULT_THEME } from './defaultTheme';

export type BrandingFormData = {
  logoFileId?: number;
  _logoUrl?: {
    resized: string;
    full: string;
  };
  theme: Theme;
};

interface ClientPortalBrandingFormProps {
  initialData: BrandingFormData;
  onSubmit: (data: BrandingFormData) => Promise<BrandingFormData>;
}

function ResetColorsButton() {
  const { onChangeCallback } = useFormContext();

  const handleReset = () => {
    if (onChangeCallback) {
      onChangeCallback({
        theme: DEFAULT_THEME,
      });
    }
  };

  return (
    <Button
      text="Reset to Default Colors"
      color={ButtonColor.GRAY}
      size={ButtonSize.SMALL}
      isFilled={true}
      onClick={handleReset}
    />
  );
}

export default function ClientPortalBrandingForm(props: ClientPortalBrandingFormProps) {
  const formInitialData: BrandingFormData = {
    logoFileId: props.initialData.logoFileId,
    _logoUrl: props.initialData._logoUrl,
    theme: props.initialData.theme || DEFAULT_THEME,
  };

  const colorValidation = {
    type: 'string' as const,
    constraints: {
      required: true,
      regex: {
        value: /^#([0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/,
        message: 'Must be a valid hex color (#RRGGBB or #RRGGBBAA)',
      },
    },
  };

  const blockStyleValidation = {
    type: 'object' as const,
    constraints: {
      required: true,
    },
    properties: {
      backgroundColor: colorValidation,
      fontColor: colorValidation,
      shadingColor: colorValidation,
      highlightColor: colorValidation,
    },
  };

  const validationSchema = {
    theme: {
      type: 'object' as const,
      constraints: {
        required: true,
      },
      properties: {
        coloredBackgroundBlock: blockStyleValidation,
        whiteBackgroundBlock: blockStyleValidation,
        grayBackgroundBlock: blockStyleValidation,
      },
    },
  };

  return (
    <FormTemplate<BrandingFormData>
      submitFormFunc={props.onSubmit}
      initialData={formInitialData}
      validationSchema={validationSchema}
      buttons={
        <SubmitButton text="Save Changes"  />
      }
    >
      <Header4>Logo</Header4>

      <Header4>Theme Colors</Header4>

      <p>
        Customize the colors used in your client portal. All colors are required.
      </p>

      <SimpleBlock>
        <ResetColorsButton />
      </SimpleBlock>

      <Header4>Colored Background Section</Header4>
      <p>
        Colors for sections with colored backgrounds (Home, Documents, Roadmap).
      </p>

      <Row6x6x>
        <ColorPickerWithText
          source="theme.coloredBackgroundBlock.backgroundColor"
          label="Background Color"
          defaultColor={DEFAULT_THEME.coloredBackgroundBlock.backgroundColor}
          hint="Background color for colored sections"
        />

        <ColorPickerWithText
          source="theme.coloredBackgroundBlock.fontColor"
          label="Font Color"
          defaultColor={DEFAULT_THEME.coloredBackgroundBlock.fontColor}
          hint="Text color on colored backgrounds"
        />
      </Row6x6x>

      <Row6x6x>
        <ColorPickerWithText
          source="theme.coloredBackgroundBlock.shadingColor"
          label="Shading Color"
          defaultColor={DEFAULT_THEME.coloredBackgroundBlock.shadingColor}
          hint="Shading color for colored sections"
        />

        <ColorPickerWithText
          source="theme.coloredBackgroundBlock.highlightColor"
          label="Highlight Color"
          defaultColor={DEFAULT_THEME.coloredBackgroundBlock.highlightColor}
          hint="Highlight color for colored sections"
        />
      </Row6x6x>

      <Header4>White Background Section</Header4>
      <p>
        Colors for sections with white backgrounds (Details, Contacts). Background color is locked to white.
      </p>

      <Row6x6x>
        <ColorPickerWithText
          source="theme.whiteBackgroundBlock.backgroundColor"
          label="Background Color"
          defaultColor={DEFAULT_THEME.whiteBackgroundBlock.backgroundColor}
          hint="Locked to white"
          isDisabled={true}
        />

        <ColorPickerWithText
          source="theme.whiteBackgroundBlock.fontColor"
          label="Font Color"
          defaultColor={DEFAULT_THEME.whiteBackgroundBlock.fontColor}
          hint="Text color on white backgrounds"
        />
      </Row6x6x>

      <Row6x6x>
        <ColorPickerWithText
          source="theme.whiteBackgroundBlock.shadingColor"
          label="Shading Color"
          defaultColor={DEFAULT_THEME.whiteBackgroundBlock.shadingColor}
          hint="Shading color for white sections"
        />

        <ColorPickerWithText
          source="theme.whiteBackgroundBlock.highlightColor"
          label="Highlight Color"
          defaultColor={DEFAULT_THEME.whiteBackgroundBlock.highlightColor}
          hint="Highlight color for white sections"
        />
      </Row6x6x>

      <Header4>Gray Background Section</Header4>
      <p>
        Colors for sections with gray backgrounds (Notes). Background color is locked to gray.
      </p>

      <Row6x6x>
        <ColorPickerWithText
          source="theme.grayBackgroundBlock.backgroundColor"
          label="Background Color"
          defaultColor={DEFAULT_THEME.grayBackgroundBlock.backgroundColor}
          hint="Locked to gray (#F5F8F9)"
          isDisabled={true}
        />

        <ColorPickerWithText
          source="theme.grayBackgroundBlock.fontColor"
          label="Font Color"
          defaultColor={DEFAULT_THEME.grayBackgroundBlock.fontColor}
          hint="Text color on gray backgrounds"
        />
      </Row6x6x>

      <Row6x6x>
        <ColorPickerWithText
          source="theme.grayBackgroundBlock.shadingColor"
          label="Shading Color"
          defaultColor={DEFAULT_THEME.grayBackgroundBlock.shadingColor}
          hint="Shading color for gray sections"
        />

        <ColorPickerWithText
          source="theme.grayBackgroundBlock.highlightColor"
          label="Highlight Color"
          defaultColor={DEFAULT_THEME.grayBackgroundBlock.highlightColor}
          hint="Highlight color for gray sections"
        />
      </Row6x6x>
    </FormTemplate>
  );
}
